import React from 'react';
import {Show, useRecordContext, useRedirect, useTranslate, usePermissions} from 'react-admin';
import {Alert, Box, Chip, Typography} from '@mui/material';
import {DeleteOutline, EditOutlined} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import StatusDot from '../components/statamic/StatusDot';
import MonoChip from '../components/statamic/MonoChip';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {useUmbrellaChoices} from '../lib/customHooks';
import {formatDateTime} from '../lib/dates';

const BooleanCell = ({value, trueLabel = 'Yes', falseLabel = 'No', variant = 'success'}) => (
    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 1}}>
        <StatusDot variant={value ? variant : 'neutral'}/>
        <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
            {value ? trueLabel : falseLabel}
        </Typography>
    </Box>
);

const OptionChips = ({options}) => (
    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
        {options.map((opt, i) => (
            <Chip
                key={`${opt}-${i}`}
                label={opt}
                size="small"
                sx={{
                    height: 22,
                    fontSize: `${t.type.caption.size}px`,
                    backgroundColor: t.color.sidebarHover,
                    border: `1px solid ${t.color.border.hairline}`,
                    color: t.color.text.primary,
                }}
            />
        ))}
    </Box>
);

const MetadataDefinitionShowContent = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const deleteConfirm = useDeleteConfirm();
    const umbrellaChoices = useUmbrellaChoices();

    if (!record) return null;

    const canEdit = !!permissions && (permissions.includes?.('admin') || permissions.includes?.('umbrella'));
    const canDelete = canEdit;

    const umbrellaName = record.parent_charity_id && umbrellaChoices
        ? umbrellaChoices.find(c => c.id === record.parent_charity_id)?.name ?? `ID: ${record.parent_charity_id}`
        : null;

    const statusVariant = record.is_active ? 'success' : 'neutral';

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('Metadata Definitions'), to: '/metadata-definitions'}}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={statusVariant}/>
                        <Typography component="span" sx={{fontFamily: t.font.mono, fontSize: `${t.type.display.size}px`, fontWeight: t.type.display.weight, lineHeight: t.type.display.lineHeight}}>
                            {record.field_name}
                        </Typography>
                    </Box>
                }
                subtitle={record.field_label}
                actions={
                    <>
                        {canEdit && (
                            <PrimaryButton
                                variant="primary"
                                startIcon={<EditOutlined sx={{fontSize: 16}}/>}
                                onClick={() => redirect('edit', 'metadata-definitions', record.id)}
                            >
                                {translate('Edit')}
                            </PrimaryButton>
                        )}
                        {canDelete && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title={translate('Basic Information')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                            <DataRow label={translate('Field Name')}>
                                {record.field_name ? <MonoChip>{record.field_name}</MonoChip> : null}
                            </DataRow>
                            <DataRow label={translate('Field Label')} value={record.field_label}/>
                            <DataRow label={translate('Entity Type')}>
                                {record.entity_type ? <MonoChip>{record.entity_type}</MonoChip> : null}
                            </DataRow>
                            <DataRow label={translate('Field Type')}>
                                {record.field_type ? <MonoChip>{record.field_type}</MonoChip> : null}
                            </DataRow>
                            <DataRow label={translate('Display Order')} value={record.display_order}/>
                        </Box>
                        {record.field_type === 'SELECT' && record.field_options?.length > 0 && (
                            <Box sx={{mt: 1, pt: 1, borderTop: `1px solid ${t.color.border.hairline}`}}>
                                <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500, mb: 1}}>
                                    {translate('Options')}
                                </Typography>
                                <OptionChips options={record.field_options}/>
                            </Box>
                        )}
                    </SectionCard>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title={translate('Field Settings')}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            <DataRow label={translate('Required Field')}>
                                <BooleanCell value={record.is_required} variant="warning"/>
                            </DataRow>
                            <DataRow label={translate('Active')}>
                                <BooleanCell value={record.is_active}/>
                            </DataRow>
                            <DataRow label={translate('Show in List View')}>
                                <BooleanCell value={record.show_in_list}/>
                            </DataRow>
                            {record.entity_type === 'GUARDIAN' && (
                                <>
                                    <DataRow label={translate('Show in Registration')}>
                                        <BooleanCell value={record.show_in_registration}/>
                                    </DataRow>
                                    {record.show_in_registration && record.field_description && (
                                        <DataRow label={translate('Field Description')} value={record.field_description}/>
                                    )}
                                </>
                            )}
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Charity Permissions')}>
                        <Alert severity="info" sx={{mb: 2, fontSize: `${t.type.small.size}px`}}>
                            {translate('Control what charity admins can do with this field')}
                        </Alert>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            <DataRow label={translate('Charity Can View')}>
                                <BooleanCell value={record.charity_can_view} variant="info"/>
                            </DataRow>
                            <DataRow label={translate('Charity Can Edit')}>
                                <BooleanCell value={record.charity_can_edit} variant="success"/>
                            </DataRow>
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Metadata')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                            <DataRow label={translate('Created')} value={formatDateTime(record.created_date)}/>
                            <DataRow label={translate('Modified')} value={formatDateTime(record.last_modified_date)}/>
                            <DataRow label={translate('Umbrella')} value={umbrellaName}/>
                        </Box>
                    </SectionCard>
                </Box>
            </Box>

            <DeleteConfirmDialog
                open={deleteConfirm.open}
                onClose={deleteConfirm.closeDialog}
                resource="metadata-definitions"
                record={record}
                recordLabel={record.field_name}
            />
        </Box>
    );
};

const MetadataDefinitionShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <MetadataDefinitionShowContent/>
    </Show>
);

export default MetadataDefinitionShowStatamic;
